import React from 'react';
import { Shield, Lock, Eye, Users, FileText, Mail, MapPin } from 'lucide-react';
interface PolicySection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const PrivacyPolicy: React.FC = () => {
  const sections: PolicySection[] = [
    {
      id: 'introduction',
      title: '1. Introduction',
      icon: <Shield className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          We at <span className="text-blue-600 font-semibold">Techno-Stack-Labs</span>, based in India, are committed to protecting your privacy. This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
        </p>
      )
    },
    {
      id: 'information-collect',
      title: '2. Information We Collect',
      icon: <Eye className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="text-blue-600 font-semibold mb-2">Personal Information:</h4>
            <p className="text-gray-700">Name, email address, phone number, company details, etc., provided via contact forms or service requests.</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="text-blue-600 font-semibold mb-2">Usage Data:</h4>
            <p className="text-gray-700">IP address, browser type, pages visited, time spent, and other analytics data.</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="text-blue-600 font-semibold mb-2">Cookies:</h4>
            <p className="text-gray-700">We use cookies and similar tracking technologies to enhance user experience.</p>
          </div>
        </div>
      )
    },
    {
      id: 'how-we-use',
      title: '3. How We Use Your Information',
      icon: <Users className="w-6 h-6" />,
      content: (
        <div className="grid gap-3">
          {[
            'To respond to inquiries and provide requested services',
            'To improve our website, services, and customer experience',
            'To send promotional emails (only with your consent)',
            'To comply with legal obligations under Indian laws'
          ].map((item, index) => (
            <div key={index} className="flex items-start space-x-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-gray-700">{item}</p>
            </div>
          ))}
        </div>
      )
    },
    {
      id: 'information-sharing',
      title: '4. Information Sharing',
      icon: <Lock className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            We do not sell or rent your personal information. We may share data with:
          </p>
          <div className="space-y-3">
            <div className="flex items-start space-x-3 bg-amber-50 p-3 rounded-lg border border-amber-200">
              <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-gray-700">Service providers and partners who assist in operations (under strict confidentiality)</p>
            </div>
            <div className="flex items-start space-x-3 bg-amber-50 p-3 rounded-lg border border-amber-200">
              <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-gray-700">Authorities or regulators if required by law or legal process</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'data-security',
      title: '5. Data Security',
      icon: <Shield className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          We use industry-standard security measures to protect your personal information. However, no method of transmission over the internet is 100% secure.
        </p>
      )
    },
    {
      id: 'your-rights',
      title: '6. Your Rights',
      icon: <FileText className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            Under applicable Indian laws, you have the right to:
          </p>
          <div className="space-y-3">
            {[
              'Access and review your personal data',
              'Request corrections or deletion',
              'Withdraw consent at any time'
            ].map((right, index) => (
              <div key={index} className="flex items-start space-x-3 bg-green-50 p-3 rounded-lg border border-green-200">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">{right}</p>
              </div>
            ))}
          </div>
          <p className="text-gray-700 mt-4">
            To exercise these rights, email us at <span className="text-blue-600 font-semibold">info@technostacklabs.com</span>.
          </p>
        </div>
      )
    },
    {
      id: 'data-retention',
      title: '7. Data Retention',
      icon: <FileText className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          We retain your information only as long as necessary for the purposes set out in this policy or as required by law.
        </p>
      )
    },
    {
      id: 'third-party-links',
      title: '8. Third-Party Links',
      icon: <Users className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          Our website may contain links to third-party sites. We are not responsible for their privacy practices or content.
        </p>
      )
    },
    {
      id: 'children-privacy',
      title: '9. Children\'s Privacy',
      icon: <Shield className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          Our services are not intended for individuals under the age of 18. We do not knowingly collect personal data from children.
        </p>
      )
    },
    {
      id: 'policy-changes',
      title: '10. Changes to This Policy',
      icon: <FileText className="w-6 h-6" />,
      content: (
        <p className="text-gray-700 leading-relaxed">
          We may update this Privacy Policy periodically. Updates will be posted on this page with a revised effective date.
        </p>
      )
    }
  ];

  return (
    <div>
    <section className="py-16 md:py-24 bg-gradient-to-r from-blue-900 to-indigo-600">
    

    <div className="text-center ">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mb-6 shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white bg-clip-text mb-4">
            Privacy Policy
          </h1>
        
      </div>
      </section>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Policy Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div
              key={section.id}
              className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl p-8 hover:bg-white/90 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10"
              style={{
                animationDelay: `${index * 100}ms`,
                animation: 'fadeInUp 0.6s ease-out forwards'
              }}
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg border border-blue-200">
                  <div className="text-blue-600">
                    {section.icon}
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">{section.title}</h2>
              </div>
              <div className="ml-16">
                {section.content}
              </div>
            </div>
          ))}

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 backdrop-blur-sm border border-blue-200 rounded-xl p-8 mt-12 shadow-lg">
            <div className="flex items-center space-x-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg border border-blue-200">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">11. Contact Us</h2>
            </div>
            <div className="ml-16 space-y-4">
              <p className="text-gray-700 leading-relaxed mb-6">
                If you have questions or concerns about this policy or your data, please contact us at:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3 bg-white/80 p-4 rounded-lg border border-gray-200 shadow-sm">
                  <Mail className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-gray-500 text-sm">Email</p>
                    <p className="text-blue-600 font-semibold">info@technostacklabs.com</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 bg-white/80 p-4 rounded-lg border border-gray-200 shadow-sm">
                  <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-gray-500 text-sm">Address</p>
                    <p className="text-gray-700">D, 107, Vyapar Marg, D Block, Sector 2, Noida, Uttar Pradesh 201301</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-16 pt-8 border-t border-gray-200">
          <p className="text-gray-500">
            Last updated: <span className="text-blue-600">[Insert Date]</span>
          </p>
        </div>
      </div>


      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;